// Pickers and cards the screens share: how they feel, how a line is said, and cards for shots,
// places, characters and props.
import { memo, type ReactNode } from "react";
import { shotFrame } from "../../engine/frame";
import { useLibrary } from "../../library/store";
import type { Camera, Line, Shot } from "../../model/episode";
import { EMOTIONS, type Emotion } from "../../model/face";
import { findAsset } from "../../model/library";
import { FrameSvg } from "../../render/FrameSvg";
import { Avatar, Figure, pictureRatio } from "./AssetPicture";
import { Icon, cap } from "./base";

// A still of a shot during a line (its camera and the speaker's feeling), for thumbnails. Only redrawn when they change.
export const ShotThumb = memo(function ShotThumb({ shot, line, camera, id }: { shot: Shot; line: Line; camera?: Camera; id: string }) {
  return <FrameSvg id={id} boil={false} frame={shotFrame(shot, line, { t: 0, clock: 0, blink: false }, camera)} />;
});

export function EmotionPicker({ who, value, onChange }: { who?: string; value: Emotion; onChange: (e: Emotion) => void }) {
  return (
    <div className="feel-grid">
      {EMOTIONS.map(e => (
        <button key={e} className={`feel${value === e ? " on" : ""}`} onClick={() => onChange(e)} aria-pressed={value === e}>
          {who ? <Avatar assetId={who} size={64} emotion={e} /> : null}
          <span>{cap(e)}</span>
        </button>
      ))}
    </div>
  );
}

// A place: its whole picture on a card, in the picture's own shape (a 9:16 sheet, or an older landscape one).
export function PlaceCard({ assetId, selected, onClick }: { assetId: string; selected?: boolean; onClick: () => void }) {
  const lib = useLibrary();
  const a = findAsset(lib, assetId);
  return (
    <button className={`pick${selected ? " on" : ""}`} onClick={onClick} aria-pressed={selected}>
      <span className="pick-art" style={{ aspectRatio: pictureRatio(a) }}><Figure assetId={assetId} /></span>
      <b>{a?.name}</b>
      {selected && <span className="tick"><Icon name="check" size={18} /></span>}
    </button>
  );
}

// A character or prop, standing on a tinted card.
export function FigureCard({ assetId, selected, onClick, sub }: { assetId: string; selected?: boolean; onClick: () => void; sub?: ReactNode }) {
  const lib = useLibrary();
  const a = findAsset(lib, assetId);
  return (
    <button className={`pick${selected ? " on" : ""}`} onClick={onClick} aria-pressed={selected}>
      <span className="pick-art figure-art" style={{ background: (a?.color ?? "#8C8C99") + "2E" }}><Figure assetId={assetId} /></span>
      <b>{a?.name}</b>
      {sub && <span className="pick-sub">{sub}</span>}
      {selected && <span className="tick"><Icon name="check" size={18} /></span>}
    </button>
  );
}

export function NewCard({ label, sub, onClick }: { label: string; sub?: string; onClick: () => void }) {
  return (
    <button className="pick new" onClick={onClick}>
      <span className="pick-art"><Icon name="plus" size={34} /></span>
      <b>{label}</b>
      {sub && <span className="pick-sub">{sub}</span>}
    </button>
  );
}

